import { Temporal } from '@js-temporal/polyfill';
import { z } from 'zod';

import { StepActivationId } from '../../BrandedIds';
import { Actor } from '../../events';

export const ResolvedStepStatus = z.enum(['completed', 'failed', 'skipped']);
export type ResolvedStepStatus = z.infer<typeof ResolvedStepStatus>;

export const UnresolvedStepStatus = z.enum(['inactive', 'active', 'reactivatedForEditing']);
export type UnresolvedStepStatus = z.infer<typeof UnresolvedStepStatus>;

export const StepStatus = z.union([ResolvedStepStatus, UnresolvedStepStatus]);
export type StepStatus = z.infer<typeof StepStatus>;

export type ActiveStepStatus = 'active' | 'reactivatedForEditing';

export const isActiveStepStatus = (status: StepStatus): status is ActiveStepStatus =>
  status === 'active' || status === 'reactivatedForEditing';

export type EditableStepStatus = Extract<ResolvedStepStatus, 'completed' | 'skipped'>;

export const isEditableStepStatus = (status: StepStatus): status is EditableStepStatus =>
  status === 'completed' || status === 'skipped';

export const isResolvedStepStatus = (status: StepStatus): status is ResolvedStepStatus =>
  ResolvedStepStatus.safeParse(status).success;

export const isUnresolvedStepStatus = (status: StepStatus): status is UnresolvedStepStatus =>
  UnresolvedStepStatus.safeParse(status).success;

interface BaseSerializedStepInstanceStatus {
  timestamp: Temporal.ZonedDateTimeLike | string;
  actor: Actor;
  manual: boolean;
}

export interface SerializedActiveStepInstanceStatus extends BaseSerializedStepInstanceStatus {
  status: ActiveStepStatus;
  activationId: StepActivationId;
}

export interface SerializedNonActiveStepInstanceStatus extends BaseSerializedStepInstanceStatus {
  status: Exclude<StepStatus, ActiveStepStatus>;
}

export type SerializedStepInstanceStatus = SerializedActiveStepInstanceStatus | SerializedNonActiveStepInstanceStatus;

export interface ActiveStepInstanceStatus extends SerializedActiveStepInstanceStatus {
  timestamp: Temporal.ZonedDateTime;
}

export interface NonActiveStepInstanceStatus extends SerializedNonActiveStepInstanceStatus {
  timestamp: Temporal.ZonedDateTime;
}

export type StepInstanceStatus = ActiveStepInstanceStatus | NonActiveStepInstanceStatus;

export const isActiveStepInstanceStatus = (status: StepInstanceStatus): status is ActiveStepInstanceStatus =>
  isActiveStepStatus(status.status);

export function reviveStepInstanceStatus(serialized: SerializedStepInstanceStatus): StepInstanceStatus {
  return {
    ...serialized,
    timestamp: Temporal.ZonedDateTime.from(serialized.timestamp),
  };
}
